/**
 * Calendly integration page.
 *
 * Auth: Personal Access Token (PAT) — customer generates from Calendly → My Account → Integrations → API & Webhooks.
 * Capabilities:
 *   - List event types (demo, support call, etc.)
 *   - Create single-use scheduling links sent to visitors in chat
 *   - Configure default event type for quick booking
 *
 * The workflow HTTP node hits POST /api/admin/Calendly/SchedulingLink and returns
 * a unique booking URL that the bot sends to the visitor.
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import IntegrationActivityLog from "../IntegrationActivityLog";

interface CalendlyStatus {
  configured: boolean;
  displayName: string;
  userName: string;
  organizationUri: string;
  defaultEventTypeUri: string;
  defaultEventTypeName: string;
  useSingleUseLinks: boolean;
}

interface EventType {
  uri: string;
  name: string;
  durationMinutes: number;
  schedulingUrl: string;
  active: boolean;
  slug: string;
}

export default function CalendlyPage() {
  const api = useApi();

  const [status, setStatus] = useState<CalendlyStatus>({
    configured: false,
    displayName: "",
    userName: "",
    organizationUri: "",
    defaultEventTypeUri: "",
    defaultEventTypeName: "",
    useSingleUseLinks: true
  });
  const [loading, setLoading]         = useState(true);
  const [token, setToken]             = useState("");
  const [displayName, setDisplayName] = useState("");
  const [defaultUri, setDefaultUri]   = useState("");
  const [defaultName, setDefaultName] = useState("");
  const [useSingleUse, setUseSingleUse] = useState(true);
  const [saving, setSaving]           = useState(false);
  const [saveError, setSaveError]     = useState<string | null>(null);
  const [eventTypes, setEventTypes]   = useState<EventType[]>([]);
  const [loadingEts, setLoadingEts]   = useState(false);
  const [showWizard, setShowWizard]   = useState(false);
  const [deleting, setDeleting]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await api.admin.get("Calendly/Status");
      const data = await res.json();
      setStatus(data);
      if (data.configured) {
        setDisplayName(data.displayName);
        setDefaultUri(data.defaultEventTypeUri);
        setDefaultName(data.defaultEventTypeName);
        setUseSingleUse(data.useSingleUseLinks);
      }
    } catch {
      /* safe */
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function loadEventTypes() {
    setLoadingEts(true);
    try {
      const res  = await api.admin.get("Calendly/EventTypes");
      const data = await res.json();
      setEventTypes(Array.isArray(data.eventTypes) ? data.eventTypes : []);
    } catch {
      setEventTypes([]);
    } finally {
      setLoadingEts(false);
    }
  }

  async function save() {
    if (!token.trim()) {
      setSaveError("Personal Access Token is required.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await api.admin.post("Calendly/Config", {
        accessToken:          token.trim(),
        displayName:          displayName.trim(),
        defaultEventTypeUri:  defaultUri.trim(),
        defaultEventTypeName: defaultName.trim(),
        useSingleUseLinks:    useSingleUse
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Save failed");
      await load();
      setToken(""); // clear token from UI after save
    } catch (err: any) {
      setSaveError(err?.message || "Failed to save. Check your token and try again.");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!confirm("Disconnect Calendly? Bots will no longer be able to create scheduling links.")) return;
    setDeleting(true);
    try {
      await api.admin.delete("Calendly/Config");
      await load();
    } catch { /* ignore */ }
    finally { setDeleting(false); }
  }

  function pickEventType(et: EventType) {
    setDefaultUri(et.uri);
    setDefaultName(et.name);
  }

  if (loading) return <div className="p-8 text-sm text-slate-400">Loading...</div>;

  return (
    <div className="p-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#006BFF] text-white text-sm font-bold">C</div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Calendly</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Send personalized Calendly booking links to visitors in chat — no back-and-forth scheduling.
          </p>
        </div>
      </div>

      {/* Calendar option callout */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 mb-5 text-xs text-slate-600">
        <p className="font-semibold text-slate-700 mb-1">Choosing a scheduling method</p>
        <p>
          <strong>Calendly</strong> is ideal when you want visitors to self-pick from your live availability, or if your team uses Google Workspace.
          {" "}If you are on <strong>Microsoft 365</strong>, you can also{" "}
          <a href="/integrations/outlook-calendar" className="text-blue-600 hover:underline">connect Outlook Calendar</a>
          {" "}to book directly on your company's calendar — no Calendly subscription required.
          {" "}Both are supported simultaneously; you can use Outlook for structured demos and Calendly for quick calls.
        </p>
      </div>

      {/* Connection status */}
      {status.configured && (
        <div className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-5 py-3 mb-5">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-green-100 text-green-600 text-xs">✓</span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-green-800">Connected to Calendly</p>
            <p className="text-xs text-green-700">{status.userName} · {status.defaultEventTypeName || "No default event type set"}</p>
          </div>
          <button
            className="text-xs text-blue-600 hover:underline"
            onClick={() => setShowWizard(true)}
          >
            Launch Wizard
          </button>
        </div>
      )}

      {/* Connect / config form */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6">
        <p className="text-sm font-semibold text-slate-700 mb-4">
          {status.configured ? "Update configuration" : "Connect Calendly"}
        </p>

        {/* PAT instructions */}
        <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-800 mb-4">
          <p className="font-semibold mb-1">How to get your Personal Access Token</p>
          <ol className="list-decimal list-inside space-y-0.5">
            <li>Go to <strong>Calendly → My Account → Integrations → API & Webhooks</strong></li>
            <li>Click <strong>Generate New Token</strong>, name it "Velaro", copy it</li>
            <li>Paste it below — we validate it immediately and never display it again</li>
          </ol>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Personal Access Token {status.configured ? "(leave blank to keep existing)" : <span className="text-red-500">*</span>}
            </label>
            <input
              type="password"
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder={status.configured ? "••••••••••••  (enter new token to replace)" : "eyJ0..."}
              value={token}
              onChange={e => setToken(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Display name (optional)</label>
            <input
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Sales Calendly"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Default event type</label>
            <p className="text-xs text-slate-400 mb-1">Used when the bot creates links without specifying a type.</p>
            <div className="flex gap-2">
              <input
                className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="30 Minute Meeting"
                value={defaultName}
                readOnly={!!defaultUri}
                onChange={e => setDefaultName(e.target.value)}
              />
              {status.configured && (
                <SecondaryBlueButton label={loadingEts ? "Loading..." : "Browse types"} onClick={loadEventTypes} disabled={loadingEts} />
              )}
            </div>

            {eventTypes.length > 0 && (
              <div className="mt-2 rounded border border-slate-200 divide-y divide-slate-100 max-h-48 overflow-y-auto">
                {eventTypes.filter(et => et.active).map(et => (
                  <button
                    key={et.uri}
                    type="button"
                    onClick={() => pickEventType(et)}
                    className={`w-full px-3 py-2 text-xs flex items-center justify-between hover:bg-slate-50 text-left ${defaultUri === et.uri ? "bg-blue-50" : ""}`}
                  >
                    <span className="font-medium text-slate-700">{et.name}</span>
                    <span className="text-slate-400">{et.durationMinutes} min</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
            <input
              type="checkbox"
              className="rounded"
              checked={useSingleUse}
              onChange={e => setUseSingleUse(e.target.checked)}
            />
            <div>
              <span className="font-medium">Single-use scheduling links</span>
              <p className="text-xs text-slate-400 font-normal">Each visitor gets a unique, one-time-use link. Prevents link sharing. Recommended.</p>
            </div>
          </label>
        </div>

        {saveError && <p className="mt-3 text-xs text-red-600">{saveError}</p>}

        <div className="flex gap-2 mt-5">
          <PrimaryBlueButton
            label={saving ? "Connecting..." : (status.configured ? "Update" : "Connect Calendly")}
            onClick={save}
            disabled={saving || (!token && !status.configured)}
          />
          {status.configured && (
            <button
              className="ml-auto text-xs text-slate-400 hover:text-red-500"
              onClick={remove}
              disabled={deleting}
            >
              {deleting ? "Disconnecting..." : "Disconnect"}
            </button>
          )}
        </div>
      </div>

      {/* Workflow integration */}
      {status.configured && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 mb-6">
          <p className="text-sm font-semibold text-slate-700 mb-3">Using this in a workflow</p>
          <p className="text-xs text-slate-500 mb-3">
            Add an <strong>HTTP node</strong> that calls the scheduling link endpoint. The bot sends the returned URL to the visitor.
          </p>
          <div className="bg-slate-900 rounded-lg p-3 text-xs text-slate-100 font-mono mb-3">
            <div className="text-slate-400 mb-1">// POST /api/admin/Calendly/SchedulingLink</div>
            {`{
  "visitorName":  "<<visitorName>>",
  "visitorEmail": "<<visitorEmail>>"
}`}
          </div>
          <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800 mb-4">
            Response includes <code>bookingUrl</code> — send it with a Send Message node:
            {' "'}Here's your personalized booking link: {"<<bookingUrl>>"}{'"'}
          </div>
          <PrimaryBlueButton label="Launch Scheduling Wizard" onClick={() => setShowWizard(true)} />
        </div>
      )}

      {/* How it works */}
      <HowItWorks />

      {/* Wizard */}
      {showWizard && (
        <StarterWorkflowWizard
          integration="calendly"
          onClose={() => setShowWizard(false)}
        />
      )}

      {/* Activity Log */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Activity Log</h2>
        <IntegrationActivityLog integrationSlug="Calendly" />
      </div>
    </div>
  );
}

// ── How it works ───────────────────────────────────────────────────────────────

function HowItWorks() {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-xl border border-slate-200 bg-white mb-6 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-6 py-4 text-sm font-medium text-slate-700 hover:bg-slate-50 text-left"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span>How does the Calendly integration work?</span>
        <span className="text-slate-400 text-xs ml-2">{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="px-6 pb-6 border-t border-slate-100 space-y-4 text-sm text-slate-600 mt-4">
          <ol className="space-y-3">
            {[
              { n: "1", title: "Visitor expresses interest", body: "The bot greets the visitor and determines they want to book a call, demo, or support session." },
              { n: "2", title: "Bot creates a unique booking link", body: "An HTTP node calls /Calendly/SchedulingLink. Velaro uses your Calendly PAT to create a single-use link for that visitor's session. Each link is unique — no sharing." },
              { n: "3", title: "Visitor books their slot", body: "The bot sends the link in chat. The visitor clicks it and picks a time from your real Calendly availability — including buffer times, limit overrides, and workflows you've configured in Calendly." },
              { n: "4", title: "Calendly handles the rest", body: "Calendly sends the calendar invite, reminder emails, and any Calendly workflows you have configured (Salesforce sync, Zoom link, HubSpot update, etc.)." }
            ].map(step => (
              <li key={step.n} className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-[#006BFF]/10 text-[#006BFF] text-xs font-bold mt-0.5">{step.n}</span>
                <div>
                  <p className="font-medium text-slate-700">{step.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-xs text-slate-600">
            <p className="font-semibold mb-1">Calendly vs Outlook Calendar — which should I use?</p>
            <table className="w-full text-xs mt-1 border-collapse">
              <thead>
                <tr className="text-slate-500">
                  <th className="text-left py-1 font-medium">Feature</th>
                  <th className="text-left py-1 font-medium">Calendly</th>
                  <th className="text-left py-1 font-medium">Outlook Calendar</th>
                </tr>
              </thead>
              <tbody className="text-slate-600">
                <tr><td className="py-0.5">Availability management</td><td>✓ Calendly manages it</td><td>✓ Built in — time blocks, working days, overrides</td></tr>
                <tr><td className="py-0.5">Buffer time between appointments</td><td>✓ Built in</td><td>✓ Built in (per appointment type)</td></tr>
                <tr><td className="py-0.5">Max horizon & min notice</td><td>✓ Built in</td><td>✓ Built in (per appointment type)</td></tr>
                <tr><td className="py-0.5">Automatic reminders</td><td>✓ Calendly sends them</td><td>Custom workflows needed</td></tr>
                <tr><td className="py-0.5">CRM sync (Salesforce, HubSpot)</td><td>✓ Calendly integrations</td><td>Via Velaro rules</td></tr>
                <tr><td className="py-0.5">Bot offers specific time slots</td><td>Visitor picks from link</td><td>✓ Bot can offer specific slots</td></tr>
                <tr><td className="py-0.5">Auto calendar invite to visitor</td><td>✓ Calendly sends it</td><td>✓ Sent via Microsoft Graph</td></tr>
                <tr><td className="py-0.5">Per-rep scheduling</td><td>Calendly routing forms</td><td>✓ Appointment types per rep</td></tr>
                <tr><td className="py-0.5">No Calendly subscription needed</td><td>No</td><td>✓ Just M365 (no Calendly account)</td></tr>
              </tbody>
            </table>
            <p className="mt-2 text-slate-500">
              On Microsoft 365?{" "}
              <a href="/integrations/outlook-calendar" className="text-blue-600 hover:underline font-medium">Connect Outlook Calendar</a>
              {" "}to book directly on your company's calendar — no Calendly subscription needed.
              Google Workspace teams can use Calendly here, or visitors booked via Outlook Calendar
              automatically receive a Google Calendar-compatible invite.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
